import {
  faCheck,
  faQuestionCircle,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import {
  DASHBOARD,
  FARM_USERS_INDEX,
  FARM_VIEW,
  HELP,
  LOGIN,
  PROFILE,
} from '../../router/routes';
import {
  farm,
  guard,
  toggleProfileMenuOpen,
  user,
} from '../../store/global-slice';
import { RootState } from '../../store/store';
import { useService } from '../../services/services';
import GlobalProgressBar from '../global-progress-bar';
import css from './index.module.css';
import Hamburger from '../common/hamburger';
import Farm from '../../interfaces/farm';
import { route } from '../../services/utils';
import SeasonSwitcher from '../common/table/season-switcher/season-switcher';
import ShowIfAllowed from '../common/show-if-allowed';
import NotificationIcon from '../notification-icon';
import Notification from './notification';

const UUID_LENGTH = 36;

export default function Topbar() {
  const { t } = useTranslation();
  const history = useHistory();
  const { authService } = useService();
  const dispatch = useDispatch();
  const { userState, isProfileMenuOpen, selectedFarm, farmUser } = useSelector(
    (state: RootState) => ({
      userState: state.global.user,
      farmUser: state.global.farmUser,
      isProfileMenuOpen: state.global.isProfileMenuOpen,
      selectedFarm: state.global.farm,
    })
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);

  const { farmId } = useParams<{ farmId: string }>();

  const toggleOpen = () => dispatch(toggleProfileMenuOpen());
  const logout = () => {
    authService.logout().subscribe(() => {
      dispatch(user(null));
      dispatch(guard(null));
      sessionStorage.clear();
      history.push(LOGIN);
    });
  };
  const switchFarm = (selectedFarm: Farm) => () => {
    dispatch(farm(selectedFarm));
    history.push(route(DASHBOARD, { farmId: selectedFarm.id }));
  };

  useEffect(() => {
    dispatch(toggleProfileMenuOpen(false));
  }, [history.location.pathname, dispatch]);

  useEffect(() => {
    if (farmId && farmId !== selectedFarm.id) {
      dispatch(farm(selectedFarm));
      history.push(route(DASHBOARD, { farmId: selectedFarm.id }));
    }
  }, [farmId, selectedFarm, history, dispatch]);

  useEffect(() => {
    if (farmId && farmId.length === UUID_LENGTH) {
      authService.notificationCount(farmId).subscribe((v) => {
        setNotificationCount(v?.unread_notifications_count);
      });
    }
  }, [authService, farmId, isModalOpen]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={css['topbar']}>
      <div className={css['content']}>
        <div className={css['season-switcher']}>
          <SeasonSwitcher />
        </div>
        <div className={css['hamburger-wrapper']}>
          <Hamburger />
        </div>
        <a
          className={css['help']}
          href={HELP}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={faQuestionCircle} />
          <span>{t('components.topbar.help')}</span>
        </a>
        <div onClick={openModal}>
          <NotificationIcon
            count={notificationCount}
            text={t('components.topbar.notifications')}
          />
        </div>
        <div className={css['divider']}>&nbsp;</div>
        <div className={css['user']} onClick={toggleOpen}>
          <div className={css['avatar']}>
            <FontAwesomeIcon icon={faUser} />
          </div>
          <div className={css['user-and-farm-info']}>
            <span className={css['username']}>{userState?.name}</span>
            <span className={css['farm-name']}>{selectedFarm?.name}</span>
          </div>
        </div>
      </div>

      <div
        className={`${css['menu-wrapper']} ${
          isProfileMenuOpen && css['opened']
        }`}
      >
        <div className={css['decorator-wrapper']}>
          <div className={css['decorator']} />
          <span className={css['decorator-sizer']}>{userState?.name}</span>
        </div>
        <div className={css['menu']}>
          <div className={css['menu-hamburger']}>
            <Hamburger
              theme="light"
              stateProp="isProfileMenuOpen"
              toggleFunc={toggleProfileMenuOpen}
            />
          </div>
          <div className={css['accounts']}>
            {Array.isArray(userState.farms)
              ? userState.farms.map((farm) => (
                  <span
                    key={farm.id}
                    className={`${css['link']} ${
                      farm.id === selectedFarm.id ? css['active'] : ''
                    } `}
                  >
                    <span onClick={switchFarm(farm)}>{farm.name}</span>
                    {farm.id === selectedFarm.id ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : null}
                  </span>
                ))
              : null}
          </div>
          <div className={css['links']}>
            <NavLink activeClassName={css['active']} to={PROFILE}>
              {t('components.topbar.profile')}
            </NavLink>
            {farmUser.owner && (
              <NavLink
                activeClassName={css['active']}
                to={route(FARM_VIEW, { farmId: selectedFarm.id })}
              >
                {t('components.topbar.farm')}
              </NavLink>
            )}
            <ShowIfAllowed ability={{ title: 'Manage farm users' }}>
              <NavLink
                activeClassName={css['active']}
                to={route(FARM_USERS_INDEX, { farmId: selectedFarm.id })}
              >
                {t('components.topbar.users')}
              </NavLink>
            </ShowIfAllowed>
          </div>
          <div className={css['logout']} onClick={logout}>
            <span className={css['link']}>{t('Logout')}</span>
          </div>
        </div>
      </div>
      <Notification isOpen={isModalOpen} onClose={closeModal} />
      <GlobalProgressBar />
    </div>
  );
}
